/**
 * Staff Availability Page
 * Mobile-friendly weekly editor for staff to set their availability preferences
 * Supports offline queue with automatic sync
 */

import React, { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  ChevronLeft,
  Plus,
  Trash2,
  Clock,
  Save,
  Loader2,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Cloud,
  CloudOff
} from 'lucide-react';
import { toast } from 'sonner';
import { useAvailabilityAction } from '@/hooks/useOfflineAction';
import { useOfflineSync } from '@/hooks/useOfflineSync';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { value: 0, label: 'Monday', short: 'Mon' },
  { value: 1, label: 'Tuesday', short: 'Tue' },
  { value: 2, label: 'Wednesday', short: 'Wed' },
  { value: 3, label: 'Thursday', short: 'Thu' },
  { value: 4, label: 'Friday', short: 'Fri' },
  { value: 5, label: 'Saturday', short: 'Sat' },
  { value: 6, label: 'Sunday', short: 'Sun' },
];

const PREFERENCES = [
  { value: 'preferred', label: 'Preferred', color: 'bg-emerald-500/20 text-emerald-400' },
  { value: 'available', label: 'Available', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'unavailable', label: 'Unavailable', color: 'bg-red-500/20 text-red-400' },
];

const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour = h.toString().padStart(2, '0');
    const minute = m.toString().padStart(2, '0');
    TIME_OPTIONS.push(`${hour}:${minute}`);
  }
}

export default function AvailabilityPage() {
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [pendingSync, setPendingSync] = useState(false);
  
  // Sheet state for adding/editing
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    day_of_week: 0,
    start_time: '09:00',
    end_time: '17:00',
    preference: 'preferred',
    notes: ''
  });

  // Offline sync hooks
  const { execute: executeAvailability, isExecuting } = useAvailabilityAction();
  const { isOnline, pendingCount } = useOfflineSync();

  const getToken = () => {
    // the staff session: the staff PIN sign-in (/staff/login or the POS; pos_token:
    // typ "staff" with the staff member's restaurant and role). A customer
    // sign-in names no restaurant and is never a staff session.
    return localStorage.getItem('pos_token');
  };

  // Get user ID for deduplication
  const getUserId = () => {
    try {
      const token = getToken();
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub || payload.user_id || payload.email;
      }
    } catch (e) {}
    return 'unknown';
  };

  const fetchAvailability = useLatestCallback(async () => {
    setLoading(true);
    setError(null);
    
    const token = getToken();
    if (!token) {
      setError('Please log in to manage your availability');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/api/availability/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Session expired. Please log in again.');
          return;
        }
        throw new Error('Failed to fetch availability');
      }

      const data = await response.json();
      setAvailability(data.items || []);
    } catch (err) {
      console.error('Error fetching availability:', err);
      setError('Unable to load availability. Please try again.');
    } finally {
      setLoading(false);
    }
  });

  const saveAvailability = async () => {
    const token = getToken();
    if (!token) {
      toast.error('Please log in to save changes');
      return;
    }

    setSaving(true);
    
    const payload = {
      items: availability.map(item => ({
        day_of_week: item.day_of_week,
        start_time: item.start_time,
        end_time: item.end_time,
        preference: item.preference,
        is_available: item.preference !== 'unavailable',
        notes: item.notes
      }))
    };

    try {
      // Use offline-capable action
      const result = await executeAvailability({
        payload,
        entityId: `weekly-${getUserId()}`,  // Semantic ID for deduplication
        userId: getUserId()
      });

      if (result.queued) {
        // Saved offline
        setPendingSync(true);
        toast.success('Availability saved offline. Will sync when online.', {
          icon: <CloudOff className="w-4 h-4" />
        });
        setHasChanges(false);
      } else if (result.ok) {
        // Saved online
        setPendingSync(false);
        toast.success('Availability saved successfully!', {
          icon: <Cloud className="w-4 h-4" />
        });
        setHasChanges(false);
        // Refresh authoritative state
        fetchAvailability();
      } else {
        throw new Error(result.error || 'Failed to save availability');
      }
    } catch (err) {
      console.error('Error saving availability:', err);
      toast.error('Failed to save availability. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddBlock = () => {
    setEditingItem(null);
    setFormData({
      day_of_week: 0,
      start_time: '09:00',
      end_time: '17:00',
      preference: 'preferred',
      notes: ''
    });
    setSheetOpen(true);
  };

  const handleEditBlock = (item, index) => {
    setEditingItem(index);
    setFormData({
      day_of_week: item.day_of_week,
      start_time: item.start_time,
      end_time: item.end_time,
      preference: item.preference,
      notes: item.notes || ''
    });
    setSheetOpen(true);
  };

  const handleSaveBlock = () => {
    if (formData.end_time <= formData.start_time) {
      toast.error('End time must be after start time');
      return;
    }

    const newItem = {
      day_of_week: formData.day_of_week,
      day_name: DAYS[formData.day_of_week].label,
      start_time: formData.start_time,
      end_time: formData.end_time,
      preference: formData.preference,
      is_available: formData.preference !== 'unavailable',
      notes: formData.notes
    };

    if (editingItem !== null) {
      const updated = [...availability];
      updated[editingItem] = newItem;
      setAvailability(updated);
    } else {
      setAvailability([...availability, newItem]);
    }

    setHasChanges(true);
    setSheetOpen(false);
  };

  const handleDeleteBlock = (index) => {
    const updated = availability.filter((_, i) => i !== index);
    setAvailability(updated);
    setHasChanges(true);
  };

  const handleClearDay = (dayOfWeek) => {
    const updated = availability.filter(item => item.day_of_week !== dayOfWeek);
    setAvailability(updated);
    setHasChanges(true);
  };

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  // Group availability by day
  const groupedByDay = DAYS.map(day => ({
    ...day,
    blocks: availability.filter(item => item.day_of_week === day.value)
  }));

  const getPreferenceStyle = (preference) => {
    const pref = PREFERENCES.find(p => p.value === preference);
    return pref ? pref.color : 'bg-gray-500/20 text-gray-400';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/staff/schedule" className="p-2 hover:bg-white/10 rounded-lg" aria-label="My schedule">
                <ChevronLeft className="w-5 h-5 text-white" />
              </Link>
              <div>
                <h1 className="text-lg font-semibold text-white">My Availability</h1>
                <p className="text-xs text-slate-400">Set your weekly preferences</p>
              </div>
            </div>
            
            <Button
              onClick={saveAvailability}
              disabled={!hasChanges || saving}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="save-availability-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Save
            </Button>
          </div>
        </div>
      </header>

      {/* Info Banner */}
      <div className="border-b border-white/10 bg-blue-500/10">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-blue-300">
              <Calendar className="w-4 h-4" />
              <span>Set your preferred working hours. Managers use this when scheduling shifts.</span>
            </div>
            {/* Offline status indicator */}
            {(pendingSync || !isOnline) && (
              <Badge variant="outline" className="text-amber-400 border-amber-400/50">
                <CloudOff className="w-3 h-3 mr-1" />
                {pendingSync ? 'Pending sync' : 'Offline'}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-orange-400 animate-spin mb-3" />
            <p className="text-slate-400">Loading availability...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
            <p className="text-white mb-2">{error}</p>
            <Button onClick={fetchAvailability} variant="outline" className="border-white/20 text-white">
              Try Again
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Add Block Button */}
            <Button
              onClick={handleAddBlock}
              className="w-full bg-white/5 border border-dashed border-white/20 text-white hover:bg-white/10"
              data-testid="add-availability-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Availability Block
            </Button>

            {/* Weekly Grid */}
            {groupedByDay.map(day => (
              <Card key={day.value} className="bg-white/5 border-white/10">
                <CardHeader className="py-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white text-base">{day.label}</CardTitle>
                    {day.blocks.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleClearDay(day.value)}
                        className="text-slate-400 hover:text-red-400 text-xs"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="py-2 px-4">
                  {day.blocks.length === 0 ? (
                    <p className="text-slate-500 text-sm py-2">No availability set</p>
                  ) : (
                    <div className="space-y-2">
                      {day.blocks.map((block, idx) => {
                        const blockIndex = availability.findIndex(
                          a => a.day_of_week === block.day_of_week && 
                               a.start_time === block.start_time && 
                               a.end_time === block.end_time
                        );
                        
                        return (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-3 rounded-lg bg-white/5 cursor-pointer hover:bg-white/10"
                            onClick={() => handleEditBlock(block, blockIndex)}
                            data-testid={`availability-block-${day.value}-${idx}`}
                          >
                            <div className="flex items-center gap-3">
                              <Clock className="w-4 h-4 text-slate-400" />
                              <span className="text-white">
                                {block.start_time} - {block.end_time}
                              </span>
                              <Badge className={getPreferenceStyle(block.preference)}>
                                {PREFERENCES.find(p => p.value === block.preference)?.label}
                              </Badge>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteBlock(blockIndex);
                              }}
                              className="text-slate-400 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {/* Has Changes Indicator */}
            {hasChanges && (
              <div className="fixed bottom-4 left-4 right-4">
                <Card className="bg-orange-500/20 border-orange-500/30">
                  <CardContent className="py-3 px-4 flex items-center justify-between">
                    <span className="text-orange-300 text-sm">You have unsaved changes</span>
                    <Button
                      size="sm"
                      onClick={saveAvailability}
                      disabled={saving}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Now'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="bg-slate-900 border-white/10 rounded-t-2xl">
          <SheetHeader className="pb-4">
            <SheetTitle className="text-white">
              {editingItem !== null ? 'Edit Availability Block' : 'Add Availability Block'}
            </SheetTitle>
          </SheetHeader>
          
          <div className="space-y-4 pb-4">
            {/* Day */}
            <div className="space-y-2">
              <Label className="text-white">Day</Label>
              <Select
                value={formData.day_of_week.toString()}
                onValueChange={(v) => setFormData({ ...formData, day_of_week: parseInt(v) })}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map(day => (
                    <SelectItem key={day.value} value={day.value.toString()}>
                      {day.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Time Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Start Time</Label>
                <Select
                  value={formData.start_time}
                  onValueChange={(v) => setFormData({ ...formData, start_time: v })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {TIME_OPTIONS.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-white">End Time</Label>
                <Select
                  value={formData.end_time}
                  onValueChange={(v) => setFormData({ ...formData, end_time: v })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {TIME_OPTIONS.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Preference */}
            <div className="space-y-2">
              <Label className="text-white">Preference</Label>
              <Select
                value={formData.preference}
                onValueChange={(v) => setFormData({ ...formData, preference: v })}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PREFERENCES.map(pref => (
                    <SelectItem key={pref.value} value={pref.value}>
                      {pref.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-white">Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes..."
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 resize-none"
                rows={2}
              />
            </div>
          </div>

          <SheetFooter>
            <Button
              onClick={handleSaveBlock}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {editingItem !== null ? 'Update Block' : 'Add Block'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
